import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Plus, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";
import { formatINR, formatDate, COMMISSION_TX_TYPES } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import StatusBadge from "../../components/StatusBadge";
import { EditModeToggle } from "../../components/editor/EditModeToggle";
import { useEditModeSession } from "../../hooks/useEditModeSession";
import LeaderboardWidget from "../../components/LeaderboardWidget";
import MilestoneProgressWidget from "../../components/MilestoneProgressWidget";

function SlotsWidget() {
  // Slots widget hidden — unlimited agreements now available
  return null;
}

function Stat({ label, value, sub, color = "" }) {
  return (
    <div className="card-base p-5">
      <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`mt-2 text-3xl font-bold font-display ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AgentHome() {
  const { user } = useAuth();
  const { isInEditMode } = useEditModeSession();
  const [rentals, setRentals] = useState([]);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    (async () => {
      const [r, w] = await Promise.all([api.get("/rentals"), api.get("/wallet")]);
      setRentals(r.data);
      setWallet(w.data);
    })();
  }, []);

  const isBrokerAgent = !!user?.broker_admin_id;
  const txns = wallet?.transactions || [];

  const active = rentals.filter(r =>
    ["active", "checkout_pending", "rating_phase1", "settlement_pending", "rating_phase2"].includes(r.status)
  ).length;

  // This month — commission only (both roles)
  const thisMonthCommission = txns
    .filter(t =>
      COMMISSION_TX_TYPES.includes(t.type) &&
      new Date(t.created_at).getMonth() === new Date().getMonth() &&
      new Date(t.created_at).getFullYear() === new Date().getFullYear()
    )
    .reduce((s, t) => s + (t.amount_paise || 0), 0);

  // Total earned — filtered by role
  const totalEarned = isBrokerAgent
    // broker-agent: commission + referral only, no brokerage
    ? txns
        .filter(t => COMMISSION_TX_TYPES.includes(t.type))
        .reduce((s, t) => s + (t.amount_paise || 0), 0)
    // solo agent: everything
    : (wallet?.total_earned_paise || 0);

  // Brokerage total (solo agent only)
  const brokerageEarned = txns
    .filter(t => t.type === "brokerage")
    .reduce((s, t) => s + (t.amount_paise || 0), 0);

  const balance = wallet?.balance_paise || 0;
  const canWithdraw = balance >= 25000; // ₹250 minimum

  return (
    <div className={isInEditMode ? "edit-mode-active" : ""}>
      <EditModeToggle pageId="agent_dashboard" />

      <div className="space-y-6" data-testid="agent-home">
        {/* Banner */}
      <div className="rounded-xl bg-orange-50 border border-orange-200 p-5">
        <h2 className="text-lg font-semibold font-display">You're all set, {user?.name?.split(" ")[0]}!</h2>
        <p className="text-sm text-gray-700 mt-1">Create your first rental agreement and earn up to ₹1,500 per deal.</p>
        <Link to="/app/agreement-builder" data-testid="banner-new-rental"
          className="inline-flex mt-3 bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-4 py-2 rounded-lg text-sm font-medium">
          Create Agreement →
        </Link>
      </div>

      <LeaderboardWidget />

      <MilestoneProgressWidget />

      {/* Stat cards — different for broker-agent vs solo */}
      {isBrokerAgent ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Stat label="Active Deals" value={active} />
          <Stat label="This Month" value={formatINR(thisMonthCommission)} color="text-orange-600" sub="Commission earned" />
          <Stat label="Ready to Withdraw" value={formatINR(balance)} color="text-emerald-600" />
          <div className="card-base p-5 bg-[#FFF4ED] border border-orange-100">
            <div className="flex items-center gap-1.5 mb-2">
              <TrendingUp className="h-3.5 w-3.5 text-[#F97316]" />
              <div className="text-xs text-[#F97316] uppercase tracking-wider">Yearly Earnings</div>
            </div>
            <div className="text-3xl font-bold font-display text-[#F97316]">{formatINR(totalEarned)}</div>
            <div className="text-xs text-gray-400 mt-1">Total commission earned</div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Stat label="Active Deals" value={active} />
          <Stat label="This Month" value={formatINR(thisMonthCommission)} color="text-orange-600" sub="Commission earned" />
          <Stat label="Ready to Withdraw" value={formatINR(balance)} color="text-emerald-600" />
          <Stat label="Brokerage Earned" value={formatINR(brokerageEarned)} sub="Dalali all time" />
        </div>
      )}

      {/* Slots widget — solo agents only */}
      {!isBrokerAgent && <SlotsWidget />}

      {/* Recent deals table */}
      <div className="card-base">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold">My recent deals</h2>
          <Link to="/app/deals" className="text-sm text-[#F97316]">View all →</Link>
        </div>
        {rentals.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">
            No deals yet.
            <div className="mt-3">
              <Link to="/app/agreement-builder" className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 rounded-lg text-sm font-medium">
                Create rental +
              </Link>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-gray-500 uppercase tracking-wider bg-gray-50">
              <tr>
                <th className="text-left px-5 py-3">Property</th>
                <th className="text-left px-5 py-3">Tenant</th>
                <th className="text-right px-5 py-3">Deposit</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rentals.slice(0, 5).map(r => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="px-5 py-3 font-medium">{r.property_address}</td>
                  <td className="px-5 py-3 text-gray-600">{r.tenants_resolved?.[0]?.user?.name}</td>
                  <td className="px-5 py-3 text-right">{formatINR(r.deposit_amount)}</td>
                  <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <Link to={`/app/rental/${r.id}`} className="text-[#F97316]">Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <Link to="/app/agreement-builder"
          className="bg-[#F97316] hover:bg-[#EA580C] btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> New Rental
        </Link>
        {canWithdraw && (
          <Link to="/app/earnings"
            className="bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-4 py-2.5 rounded-lg text-sm font-medium inline-flex items-center gap-2">
            <Wallet className="h-4 w-4" /> Withdraw Earnings
          </Link>
        )}
      </div>
      </div>
    </div>
  );
}